/**
 * Pricing page — Bundle 4.
 *
 * Public-facing plan comparison table. Accessible at /pricing without login.
 * Authenticated users are CTAed to /billing; unauthenticated to /register.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { buildBillingUpgradePath } from '../lib/plans';
import { usePageMeta } from '../lib/page-meta';
import PublicSiteChrome from '../components/layout/PublicSiteChrome';
import { buildBreadcrumbSchema, buildWebPageSchema, SEO_BRAND_NAME } from '../lib/seo';

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const C = {
  page: 'var(--acc-color-page)',
  text: 'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  border: 'var(--acc-color-border)',
  accent: 'var(--acc-color-primary)',
  success: 'var(--acc-color-success)',
  surface: 'var(--acc-color-surface)',
  dark: 'var(--acc-color-primary-strong)',
};

const DEMO_REQUEST_HREF = 'mailto:sales@ablecomply.com?subject=AbleComply%20Enterprise%20Enquiry';

type PlanId = 'free' | 'starter' | 'growth' | 'enterprise';

interface FeatureRow {
  id: string;
  label: string;
  free: string | boolean;
  starter: string | boolean;
  growth: string | boolean;
  enterprise: string | boolean;
}

interface PlanConfig {
  id: PlanId;
  price?: string;
  priceKey?: string;
  highlighted?: boolean;
}

interface Plan {
  id: PlanId;
  name: string;
  price: string;
  priceNote: string;
  tagline: string;
  highlighted?: boolean;
  ctaLabel: string;
  ctaHref: string;
  external?: boolean;
}

const PLAN_CONFIGS: PlanConfig[] = [
  {
    id: 'free',
    price: '$0',
  },
  {
    id: 'starter',
    price: '$49',
  },
  {
    id: 'growth',
    price: '$149',
    highlighted: true,
  },
  {
    id: 'enterprise',
    priceKey: 'pricing.plans.enterprise.price',
  },
];

// ---------------------------------------------------------------------------
// Cell renderer
// ---------------------------------------------------------------------------

function Cell({ value, highlight }: { value: string | boolean; highlight: boolean }) {
  if (typeof value === 'boolean') {
    return (
      <td style={{
        padding: '11px 20px',
        textAlign: 'center',
        borderBottom: `1px solid ${C.border}`,
        background: highlight ? 'var(--acc-color-gold-soft)' : 'transparent',
      }}>
        {value
          ? <span style={{ color: C.success, fontSize: 16 }}>✓</span>
          : <span style={{ color: '#d1d5db', fontSize: 14 }}>—</span>}
      </td>
    );
  }

  return (
    <td style={{
      padding: '11px 20px',
      textAlign: 'center',
      fontSize: 13,
      color: C.text,
      borderBottom: `1px solid ${C.border}`,
      background: highlight ? 'var(--acc-color-gold-soft)' : 'transparent',
    }}>
      {value}
    </td>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function PricingPage() {
  const { t } = useTranslation('marketing');
  const { isAuthenticated } = useAuth();

  const featureRows = t('pricing.table.rows', { returnObjects: true }) as FeatureRow[];

  usePageMeta({
    title: t('pricing.meta.title'),
    description: t('pricing.meta.description'),
    canonicalPath: '/pricing',
    structuredData: [
      buildWebPageSchema({
        title: t('pricing.hero.title'),
        description: t('pricing.meta.description'),
        path: '/pricing',
        pageType: 'CollectionPage',
      }),
      buildBreadcrumbSchema([
        { name: SEO_BRAND_NAME, path: '/' },
        { name: t('header.nav.pricing'), path: '/pricing' },
      ]),
    ],
  });

  const resolvedPlans: Plan[] = PLAN_CONFIGS.map((plan) => {
    const planKey = `pricing.plans.${plan.id}`;
    const priceNoteKey = `${planKey}.priceNote`;
    const guestCtaLabel = plan.id === 'free'
      ? t('actions.getStarted')
      : plan.id === 'enterprise'
        ? t('actions.requestDemo')
        : t('actions.startTrial');
    const authenticatedCtaLabel = plan.id === 'free'
      ? t('actions.getStarted')
      : plan.id === 'enterprise'
        ? t('actions.requestDemo')
        : t('actions.upgrade');
    const resolvedPriceNote = t(priceNoteKey, { defaultValue: '' });

    return {
      id: plan.id,
      name: t(`${planKey}.name`),
      price: plan.price ?? t(plan.priceKey ?? `${planKey}.price`),
      priceNote: resolvedPriceNote === priceNoteKey ? '' : resolvedPriceNote,
      tagline: t(`${planKey}.tagline`),
      highlighted: plan.highlighted,
      ctaLabel: isAuthenticated ? authenticatedCtaLabel : guestCtaLabel,
      ctaHref:
        plan.id === 'enterprise'
          ? DEMO_REQUEST_HREF
          : isAuthenticated && plan.id !== 'free'
            ? buildBillingUpgradePath(plan.id, 'pricing')
            : '/register',
      external: plan.id === 'enterprise',
    };
  });

  const footerPrimaryHref = isAuthenticated
    ? buildBillingUpgradePath('starter', 'pricing-footer')
    : '/register';

  return (
    <PublicSiteChrome>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 28px 56px' }}>
        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <h1 style={{
            fontSize: 40,
            fontWeight: 900,
            color: C.text,
            margin: 0,
            marginBottom: 14,
            letterSpacing: '-0.05em',
          }}>
            {t('pricing.hero.title')}
          </h1>
          <p style={{
            fontSize: 16,
            color: C.muted,
            margin: 0,
            maxWidth: 620,
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: 1.75,
          }}>
            {t('pricing.hero.description')}
          </p>
          <div style={{
            marginTop: 18,
            display: 'grid',
            gap: 6,
            justifyItems: 'center',
          }}>
            <div style={{
              maxWidth: 720,
              color: C.text,
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.6,
            }}>
              {t('pricing.clarification.primary')}
            </div>
            <div style={{
              maxWidth: 720,
              color: C.muted,
              fontSize: 13,
              lineHeight: 1.6,
            }}>
              {t('pricing.clarification.secondary')}
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
          marginBottom: 40,
        }}>
          {resolvedPlans.map((plan) => (
            <div
              key={plan.id}
              style={{
                background: plan.highlighted ? 'var(--acc-color-gold-soft)' : C.surface,
                border: plan.highlighted ? `2px solid ${C.accent}` : `1px solid ${C.border}`,
                borderRadius: 20,
                padding: '22px 20px',
                position: 'relative',
                boxShadow: 'var(--acc-shadow-sm)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {plan.highlighted && (
                <div style={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--acc-color-gold)',
                  color: '#3b2b08',
                  fontSize: 10,
                  fontWeight: 800,
                  padding: '3px 10px',
                  borderRadius: 99,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}>
                  {t('pricing.badges.mostPopular')}
                </div>
              )}
              <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 4 }}>{plan.name}</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, minHeight: 58, lineHeight: 1.55 }}>
                {plan.tagline}
              </div>
              <div style={{ marginBottom: 18 }}>
                <span style={{ fontSize: 26, fontWeight: 900, color: C.text }}>{plan.price}</span>
                {plan.priceNote && (
                  <span style={{ fontSize: 13, color: C.muted, marginLeft: 3 }}>{plan.priceNote}</span>
                )}
              </div>
              <div style={{ marginTop: 'auto' }}>
                {plan.external ? (
                  <a
                    href={plan.ctaHref}
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      padding: '9px 0',
                      background: C.dark,
                      color: '#fff',
                      borderRadius: 12,
                      textDecoration: 'none',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {plan.ctaLabel}
                  </a>
                ) : (
                  <Link
                    to={plan.ctaHref}
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      padding: '9px 0',
                      background: plan.highlighted ? C.accent : C.dark,
                      color: '#fff',
                      borderRadius: 12,
                      textDecoration: 'none',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {plan.ctaLabel}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 20,
          overflowX: 'auto',
          boxShadow: 'var(--acc-shadow-sm)',
        }}>
          <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{
                  padding: '12px 20px',
                  textAlign: 'left',
                  fontWeight: 700,
                  fontSize: 12,
                  color: C.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  borderBottom: `1px solid ${C.border}`,
                  width: '30%',
                }}>
                  {t('pricing.table.featureHeader')}
                </th>
                {resolvedPlans.map((plan) => (
                  <th
                    key={plan.id}
                    style={{
                      padding: '12px 20px',
                      fontWeight: 700,
                      fontSize: 13,
                      color: plan.highlighted ? C.accent : C.text,
                      textAlign: 'center',
                      borderBottom: `1px solid ${C.border}`,
                      background: plan.highlighted ? 'var(--acc-color-gold-soft)' : 'transparent',
                    }}
                  >
                    {plan.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {featureRows.map((row) => (
                <tr key={row.id}>
                  <td style={{
                    padding: '11px 20px',
                    fontSize: 13,
                    color: C.text,
                    borderBottom: `1px solid ${C.border}`,
                  }}>
                    {row.label}
                  </td>
                  <Cell value={row.free} highlight={false} />
                  <Cell value={row.starter} highlight={false} />
                  <Cell value={row.growth} highlight={true} />
                  <Cell value={row.enterprise} highlight={false} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: 48,
          padding: '32px 24px',
          background: C.surface,
          borderRadius: 14,
          border: `1px solid ${C.border}`,
        }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: C.text, marginBottom: 8 }}>
            {t('pricing.footerCta.title')}
          </div>
          <p style={{ color: C.muted, fontSize: 14, margin: '0 0 20px' }}>
            {t('pricing.footerCta.description')}
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to={footerPrimaryHref}
              style={{
                padding: '10px 24px',
                background: C.accent,
                color: '#fff',
                borderRadius: 8,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {isAuthenticated ? t('actions.viewBilling') : t('actions.startForFree')}
            </Link>
            <a
              href="mailto:sales@ablecomply.com?subject=AbleComply%20Demo%20Request"
              style={{
                padding: '10px 24px',
                background: 'transparent',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {t('actions.requestDemo')}
            </a>
          </div>
        </div>
      </div>
    </PublicSiteChrome>
  );
}
